/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

type QnaMakerChatTestMessageProps = {
    message: React.ReactNode | string;
    isShortMessage?: boolean;
    showMessageTitle?: boolean;
    isQuestionMessage: boolean;
    className?: string;
    isFollowUpPrompt?: boolean;
    onInspectClick?: () => void;
    onFollowUpPromptClicked?: () => void;
};

const messages = defineMessages({
    inspect: { id: 'qna.qnaMakerChatTestMessage.inspect', defaultMessage: 'Inspect' },
    longAnswer: { id: 'qna.qnaMakerChatTestMessage.longAnswer', defaultMessage: 'Long answer' },
    shortAnswer: { id: 'qna.qnaMakerChatTestMessage.shortAnswer', defaultMessage: 'Short answer' }
});

const theme = getScopedTheme('QnaMakerChatTestBlade');
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: {
        padding: '12px',
        maxWidth: '80%',
        borderRadius: '10px',
        overflowWrap: 'anywhere',
        border: `1px solid ${theme.messageBorderColor}`
    }
});

export const QnaMakerChatTestMessage = observer((props: QnaMakerChatTestMessageProps) => {
    const {
        message,
        isFollowUpPrompt = false,
        onFollowUpPromptClicked,
        isQuestionMessage,
        className = '',
        onInspectClick,
        showMessageTitle = false,
        isShortMessage = false
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    return (
        <Stack className={className} horizontalAlign={isQuestionMessage ? 'end' : 'start'} tokens={{ childrenGap: 2 }}>
            {showMessageTitle && (
                <StackItem dir={languageDirection} align="start">
                    <LuisText variant="smallPlus">{localize(isShortMessage ? messages.shortAnswer : messages.longAnswer)}</LuisText>
                </StackItem>
            )}
            <Stack grow dir={languageDirection} styles={containerStyles} horizontalAlign={isQuestionMessage ? 'end' : 'start'}>
                {isFollowUpPrompt ? <Link onClick={onFollowUpPromptClicked}>{message}</Link> : message}
            </Stack>

            {isQuestionMessage && <Link onClick={onInspectClick}>{localize(messages.inspect)}</Link>}
        </Stack>
    );
});
